const BASE = import.meta.env.VITE_API_URL || ''

export type Ticker = {
  symbol: string; base: string; quote: string; faName: string; enName: string
  color: string; rank: number; precision: number
  last: number; open: number; high: number; low: number
  change: number; volume: number; bid: number; ask: number
  quoteVolume: number; sparkline: number[]
}

export class ApiError extends Error {
  status: number
  /**
   * مقدار خامِ detail از بک‌اند.
   *
   * بعضی پاسخ‌ها به‌جای متن، یک شیء برمی‌گردانند (مثلاً «کد پیامکی لازم است» به
   * همراه شماره ماسک‌شده و زمان انقضا). قبلاً این شیء دور ریخته می‌شد و صفحه
   * فقط «خطای نامشخص» می‌دید.
   */
  detail: any
  constructor(message: string, status: number, detail?: any) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

function token(): string | null {
  return localStorage.getItem('az_token')
}

export async function api<T = any>(
  path: string,
  options: RequestInit & { body?: any } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers.Authorization = `Bearer ${t}`

  const res = await fetch(`${BASE}/api${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  // نشستِ منقضی: توکن را دور می‌ریزیم و به بقیه برنامه خبر می‌دهیم.
  // بدون این خبر، فروشگاه همچنان کاربر را «وارد شده» می‌دید و صفحه‌ها بی‌صدا
  // خالی می‌ماندند — کاربر فکر می‌کرد «صفحه باز نمی‌شود».
  // مسیرهای ورود/ثبت‌نام استثنا هستند: ۴۰۱ آن‌ها یعنی «رمز اشتباه» یا
  // «کد دومرحله‌ای لازم است»، نه پایان نشست.
  const authFlow = path.startsWith('/auth/login') || path.startsWith('/auth/register')
                   || path.startsWith('/auth/otp')
  if (res.status === 401 && !authFlow) {
    const had = !!token()
    localStorage.removeItem('az_token')
    if (had && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('arzino:unauthorized'))
    }
  }
  const text = await res.text()
  let data: any = null
  let unparsable = false
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    // پاسخ JSON نبود (مثلاً صفحه خطای پراکسی) — متن خام را نگه می‌داریم
    data = text ? { detail: text.slice(0, 300) } : null
    unparsable = true
  }

  // پاسخ ۲۰۰ که JSON نیست، **موفق نیست**. تا پیش از این همان شیء ساختگیِ
  // {detail: …} به‌جای داده واقعی برگردانده می‌شد و صفحه‌ای که مثلاً روی
  // `data.items` حساب کرده بود، با استثنا زمین می‌خورد — خطایی که هیچ ربطی
  // به صفحه نداشت و ردیابی‌اش تقریباً غیرممکن بود.
  if (res.ok && unparsable) {
    throw new ApiError('پاسخ سرور خوانا نبود؛ لطفاً دوباره تلاش کنید', res.status, data?.detail)
  }
  if (!res.ok) {
    const raw = (data && (data.detail ?? data.message)) ?? null
    const msg = raw ?? 'خطایی رخ داد، دوباره تلاش کنید'
    const message = typeof msg === 'string'
      ? msg
      : (msg && typeof msg === 'object' && typeof msg.message === 'string')
        ? msg.message
        : 'خطای نامشخص'
    throw new ApiError(message, res.status, raw)
  }
  return data as T
}

export const get = <T = any>(p: string) => api<T>(p)
export const post = <T = any>(p: string, body?: any) => api<T>(p, { method: 'POST', body })
export const patch = <T = any>(p: string, body?: any) => api<T>(p, { method: 'PATCH', body })
export const put = <T = any>(p: string, body?: any) => api<T>(p, { method: 'PUT', body })
export const del = <T = any>(p: string) => api<T>(p, { method: 'DELETE' })

/** ساخت رشته کوئری از یک شیء (مقادیر تهی حذف می‌شوند) */
export function qs(params: Record<string, any>): string {
  const s = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return
    s.append(k, String(v))
  })
  const str = s.toString()
  return str ? `?${str}` : ''
}

/** اتصال زنده به قیمت‌ها */
export function openMarketSocket(
  onMessage: (data: any) => void,
  symbol?: string,
): { close: () => void; subscribe: (s: string) => void } {
  let ws: WebSocket | null = null
  let closed = false
  let retry = 0
  let timer: number | undefined
  let current = symbol

  const connect = () => {
    if (closed) return
    // اگر اتصال سالمی برقرار است، اتصال موازی نساز
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return

    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const host = BASE ? BASE.replace(/^https?:\/\//, '') : location.host
    ws = new WebSocket(`${proto}://${host}/ws/market`)
    ws.onopen = () => {
      retry = 0
      if (current) ws?.send(JSON.stringify({ type: 'subscribe', symbol: current }))
    }
    ws.onmessage = (e) => {
      try {
        onMessage(JSON.parse(e.data))
      } catch {
        /* ignore */
      }
    }
    ws.onclose = () => {
      if (closed) return
      retry += 1
      clearTimeout(timer)
      timer = setTimeout(connect, Math.min(1000 * retry, 8000)) as unknown as number
    }
    ws.onerror = () => ws?.close()
  }

  /**
   * وقتی کاربر به تب سایت برمی‌گردد، مرورگر تایمرهای پس‌زمینه را کند کرده و ممکن
   * است اتصال قطع شده باشد. اینجا بدون معطلی دوباره وصل می‌شویم تا کاربر با
   * صفحه‌ای که قیمت‌هایش به‌روز نمی‌شود روبه‌رو نشود.
   */
  const onWake = () => {
    if (closed || document.visibilityState !== 'visible') return
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      retry = 0
      clearTimeout(timer)
      connect()
    }
  }
  document.addEventListener('visibilitychange', onWake)
  window.addEventListener('online', onWake)
  window.addEventListener('pageshow', onWake)

  connect()

  return {
    close: () => {
      closed = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('online', onWake)
      window.removeEventListener('pageshow', onWake)
      ws?.close()
    },
    subscribe: (s: string) => {
      current = s
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'subscribe', symbol: s }))
    },
  }
}
